import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { AdminSession } from '@/types';
import { adminApi } from '@/lib/api';

interface AuthContextValue {
  session: AdminSession | null;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    mfaCode?: string,
    mfaChallenge?: string,
  ) => Promise<{ ok: boolean; error?: string; requiresMfa?: boolean; mfaChallenge?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token) { setSession(null); return; }
    const result = await adminApi.get<{ admin: AdminSession }>('/auth/me');
    if (result.ok) {
      setSession((result as any).admin);
    } else {
      // Try to refresh using the stored refresh token
      const refreshToken = localStorage.getItem('admin_refresh_token');
      if (refreshToken) {
        const refreshResult = await adminApi.post<{ accessToken: string }>('/auth/refresh', { refreshToken });
        if (refreshResult.ok) {
          localStorage.setItem('admin_access_token', (refreshResult as any).accessToken);
          const meResult = await adminApi.get<{ admin: AdminSession }>('/auth/me');
          if (meResult.ok) { setSession((meResult as any).admin); return; }
        }
      }
      setSession(null);
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setIsLoading(false));
  }, [refresh]);

  const login = async (
    email: string,
    password: string,
    mfaCode?: string,
    mfaChallenge?: string,
  ) => {
    // Step 2: MFA verification
    if (mfaCode && mfaChallenge) {
      const result = await adminApi.post<{
        accessToken: string;
        refreshToken: string;
        admin: AdminSession;
      }>('/auth/mfa/verify', { mfaChallenge, code: mfaCode });
      if (!result.ok) return { ok: false, error: result.error };
      localStorage.setItem('admin_access_token', (result as any).accessToken);
      localStorage.setItem('admin_refresh_token', (result as any).refreshToken);
      setSession((result as any).admin);
      return { ok: true };
    }

    // Step 1: Email + password login
    const result = await adminApi.post<{
      requiresMfa: boolean;
      mfaChallenge?: string;
      accessToken?: string;
      refreshToken?: string;
      admin?: AdminSession;
    }>('/auth/login', { email, password });

    if (!result.ok) return { ok: false, error: result.error };

    if ((result as any).requiresMfa) {
      return { ok: false, requiresMfa: true, mfaChallenge: (result as any).mfaChallenge };
    }

    if ((result as any).accessToken && (result as any).admin) {
      localStorage.setItem('admin_access_token', (result as any).accessToken);
      if ((result as any).refreshToken) {
        localStorage.setItem('admin_refresh_token', (result as any).refreshToken);
      }
      setSession((result as any).admin as unknown as AdminSession);
      return { ok: true };
    }

    return { ok: false, error: 'Unexpected response from server' };
  };

  const logout = async () => {
    await adminApi.post('/auth/logout', {});
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ session, isLoading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
